/**
 * SIL-2335 - the ONE place a bench stage outcome is classified.
 *
 * A stage outcome is a loose tuple (status, errorReason, destructive, wipeTicketId), posted by an agent or stored in
 * bench_run_stage. `stageVerdict` turns either form into a closed verdict; every grader and client view reads only that
 * verdict, never the tuple, so no caller combines its own helpers in its own order.
 *
 * The reason vocabulary is `stage-reasons.json` (this directory), the source the bench agent loads at build time
 * (apps/bench-agent/src/stage_reasons.rs). TS reads the checked-in mirror stage-reasons.ts (no JSON import, see there), pinned to
 * the JSON by a unit test; stage-reasons-source.test.ts (API) proves the live SQL functions bench_stage_not_run_reasons() /
 * bench_stage_server_assigned_reasons() match it. The wipe-gate reasons are TS-only (the agent and the database do not read them).
 */
import { NOT_RUN_REASON_LIST, SERVER_ASSIGNED_REASON_LIST, WIPE_GATE_REASONS, WIPE_INCONCLUSIVE_REASONS } from './stage-reasons.js';

const NOT_RUN_REASONS: ReadonlySet<string> = new Set(NOT_RUN_REASON_LIST);
const SERVER_ASSIGNED_REASONS: ReadonlySet<string> = new Set(SERVER_ASSIGNED_REASON_LIST);

/** True for a reason the server (never an agent) assigns. */
export function isServerAssignedReason(reason: string | null | undefined): boolean {
  return reason != null && SERVER_ASSIGNED_REASONS.has(reason);
}

/** Gate reasons that carry detail after a colon: `identity_unreadable: <io error>`. */
const WIPE_GATE_REASON_PREFIXES = ['identity_unreadable:', 'wipe_ticket_failed:'];

/**
 * Why a stage did not run:
 *  - tool: the executable / GPU tool pack is not on the image or was refused (not-run reasons; any stage, destructive or not)
 *  - wipe_gate: a destructive stage the wipe gate refused before the tool ran (it refuses BEFORE asking for a ticket, so a
 *    stage that carries a wipe ticket got past the gate and is graded normally)
 *  - server_gated: the server holds the stage back (a GPU-pack stage whose run segment has no confirmation record)
 */
export type NotRunCause = 'tool' | 'wipe_gate' | 'server_gated' | 'wipe_inconclusive';

/** The closed verdict. `skipped` is a declined / skipped stage: it ran nothing and proves nothing, but is not a not-run gap. */
export type StageVerdict =
  | { kind: 'ran_pass' }
  | { kind: 'ran_fail' }
  | { kind: 'skipped' }
  | { kind: 'not_run'; cause: NotRunCause };

/**
 * What a verdict needs from a stage. Accepts the posted shape (BenchStageResult) and the stored row (status is a plain string).
 */
export interface StageFacts {
  status: string;
  errorReason?: string | null;
  /** Required: each caller decides explicitly (a caller that has no wipe facts passes `false` / `null`, which can never be a wipe_gate). */
  destructive: boolean;
  wipeTicketId: string | null;
}

export interface StageVerdictOptions {
  /** The server's gate (bench_gpu_pack_gated_stage_seqs) holds this stage back. */
  serverGated?: boolean;
}

/**
 * Classify a stage outcome. Order matters and lives only here: a tool not-run reason wins (it keeps the agent's precise reason
 * even when the server also gates the stage), then the server gate, then the wipe-gate refusal, then the status.
 * An unknown status string is a failure (fail closed), never a pass.
 */
export function stageVerdict(s: StageFacts, opts: StageVerdictOptions = {}): StageVerdict {
  const reason = s.errorReason ?? null;
  if (s.status === 'error' && reason !== null && NOT_RUN_REASONS.has(reason)) return { kind: 'not_run', cause: 'tool' };
  if (opts.serverGated) return { kind: 'not_run', cause: 'server_gated' };
  // SIL-2370: an inconclusive wipe (ticketed or not) proves nothing and is not a fault of the drive.
  if (s.destructive && s.status === 'error' && reason !== null && WIPE_INCONCLUSIVE_REASONS.has(reason)) {
    return { kind: 'not_run', cause: 'wipe_inconclusive' };
  }
  if (
    s.destructive && !s.wipeTicketId && s.status === 'error' && reason !== null && reason !== ''
    && (WIPE_GATE_REASONS.has(reason) || WIPE_GATE_REASON_PREFIXES.some((p) => reason.startsWith(p)))
  ) return { kind: 'not_run', cause: 'wipe_gate' };
  if (s.status === 'pass') return { kind: 'ran_pass' };
  if (s.status === 'skipped') return { kind: 'skipped' };
  return { kind: 'ran_fail' };
}
